import SEO from "@/components/SEO";
import { FadeUp, Eyebrow, DrawLine } from "@/components/Reveal";

const COLORS = [
  { name: "Nero", hex: "#080808" },
  { name: "Antracite", hex: "#111111" },
  { name: "Oro", hex: "#C6A96B" },
  { name: "Avorio", hex: "#F5F3EE" },
  { name: "Grigio", hex: "#A1A1A1" },
];

const Tile = ({ bg = "dark", title, sub, img, alt, downloads, testid }) => (
  <FadeUp className="h-full">
    <div
      data-testid={testid}
      className={`flex h-full flex-col border border-white/10 ${bg === "dark" ? "bg-ink" : "bg-bone"}`}
    >
      <div
        className={`flex flex-1 items-center justify-center p-8 ${bg === "dark" ? "bg-ink" : "bg-bone"}`}
      >
        <img src={img} alt={alt} loading="lazy" className="max-h-36 w-auto max-w-full object-contain" />
      </div>
      <div className="border-t border-white/10 p-8">
        <h3 className={`font-serif text-lg tracking-wide ${bg === "dark" ? "text-bone" : "text-ink"}`}>
          {title}
        </h3>
        <p className={`mt-2 text-xs font-light ${bg === "dark" ? "text-mute" : "text-ink/60"}`}>
          {sub}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {downloads.map((d) => (
            <a
              key={d.file}
              href={d.file}
              download
              data-testid={`${testid}-download-${d.label.toLowerCase()}`}
              className={`inline-flex items-center gap-2 border px-4 py-2 text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                bg === "dark"
                  ? "border-gold/40 text-gold hover:bg-gold hover:text-ink"
                  : "border-ink/30 text-ink hover:bg-ink hover:text-bone"
              }`}
            >
              Scarica {d.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  </FadeUp>
);

export default function BrandAssets() {
  return (
    <>
      <SEO
        title="Logo & Marchio | Zaichauffer – Area stampa"
        description="Scarica il logo Zaichauffer in alta definizione: versione chiara su sfondo scuro, versione nera su sfondo chiaro, emblema e file vettoriali SVG."
      />
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-40 md:pt-52" data-testid="brand-page">
        <FadeUp>
          <Eyebrow>Area stampa · Identità visiva</Eyebrow>
          <h1 className="mt-10 font-serif text-4xl leading-[1.08] tracking-tight text-bone sm:text-5xl lg:text-6xl">
            LOGO &amp; MARCHIO.
          </h1>
          <DrawLine className="mt-8" />
          <p className="mt-8 max-w-xl text-[15px] font-light leading-relaxed text-mute">
            Le versioni ufficiali del marchio Zaichauffer: lo scudo con la
            vettura e la strada in prospettiva, in alta definizione, pronte
            per stampa, social, web e documenti.
          </p>
        </FadeUp>

        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          <Tile
            bg="dark"
            testid="brand-logo-chiaro"
            title="Logo completo — versione chiara"
            sub="Per sfondi scuri (nero e antracite). PNG in alta risoluzione 2172×724."
            img="/brand/zaichauffer-logo-sfondo-nero.png"
            alt="Logo definitivo Zaichauffer con scudo, versione chiara su sfondo scuro"
            downloads={[{ label: "PNG HD", file: "/brand/zaichauffer-logo-sfondo-nero.png" }]}
          />
          <Tile
            bg="light"
            testid="brand-logo-nero"
            title="Logo completo — versione nera"
            sub="Per sfondi chiari (avorio, bianco, carta). PNG in alta risoluzione 2172×724."
            img="/brand/zaichauffer-logo-sfondo-chiaro.png"
            alt="Logo definitivo Zaichauffer con scudo, versione nera su sfondo chiaro"
            downloads={[{ label: "PNG HD", file: "/brand/zaichauffer-logo-sfondo-chiaro.png" }]}
          />
          <Tile
            bg="dark"
            testid="brand-logo-emblema"
            title="Emblema — Scudo con vettura"
            sub="Per profili social, documenti e spazi ridotti. PNG quadrato 1254×1254, margine pronto per il ritaglio circolare."
            img="/brand/zaichauffer-emblema-sfondo-nero.png"
            alt="Emblema definitivo Zaichauffer: scudo oro con vettura frontale e strada in prospettiva"
            downloads={[
              { label: "PNG HD", file: "/brand/zaichauffer-emblema-sfondo-nero.png" },
              { label: "Trasparente", file: "/brand/zaichauffer-emblema-trasparente.png" },
            ]}
          />
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-16 border border-white/10 bg-coal p-10 md:p-14" data-testid="brand-hd-pack">
            <h2 className="font-serif text-2xl tracking-wide text-bone">PACCHETTO PNG — TRE VERSIONI</h2>
            <DrawLine className="mt-6" />
            <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-mute">
              Logo completo (2172×724) ed emblema (1254×1254) in alta
              definizione: su sfondo nero, su sfondo chiaro e con fondo
              trasparente. Includo anche l'icona del sito e il kit completo in
              un unico ZIP pronto da condividere.
            </p>
            <div className="mt-10 grid gap-10 md:grid-cols-2">
              <div>
                <h3 className="label-cap !text-[9px]">Logo completo</h3>
                <div className="mt-5 flex flex-wrap gap-3">
                  {[
                    { l: "Sfondo nero", f: "/brand/zaichauffer-logo-sfondo-nero.png", t: "download-hd-completo-scuro" },
                    { l: "Sfondo chiaro", f: "/brand/zaichauffer-logo-sfondo-chiaro.png", t: "download-hd-completo-chiaro" },
                    { l: "Trasparente", f: "/brand/zaichauffer-logo-trasparente.png", t: "download-hd-completo-scontornato" },
                  ].map((d) => (
                    <a
                      key={d.f}
                      href={d.f}
                      download
                      data-testid={d.t}
                      className="inline-flex items-center border border-gold/40 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                    >
                      {d.l}
                    </a>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="label-cap !text-[9px]">Emblema — Scudo</h3>
                <div className="mt-5 flex flex-wrap gap-3">
                  {[
                    { l: "Sfondo nero", f: "/brand/zaichauffer-emblema-sfondo-nero.png", t: "download-hd-emblema-scuro" },
                    { l: "Sfondo chiaro", f: "/brand/zaichauffer-emblema-sfondo-chiaro.png", t: "download-hd-emblema-chiaro" },
                    { l: "Trasparente", f: "/brand/zaichauffer-emblema-trasparente.png", t: "download-hd-emblema-scontornato" },
                  ].map((d) => (
                    <a
                      key={d.f}
                      href={d.f}
                      download
                      data-testid={d.t}
                      className="inline-flex items-center border border-gold/40 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                    >
                      {d.l}
                    </a>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-10 flex flex-col items-start gap-6 border-t border-white/10 pt-10 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="label-cap !text-[9px]">Icona del sito</h3>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href="/brand/favicon.ico"
                    download
                    data-testid="download-favicon"
                    className="inline-flex items-center border border-gold/40 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                  >
                    Favicon .ico
                  </a>
                  <a
                    href="/brand/zaichauffer-icona-sito-512.png"
                    download
                    data-testid="download-icona-512"
                    className="inline-flex items-center border border-gold/40 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                  >
                    Icona 512
                  </a>
                </div>
              </div>
              <a
                href="/brand/zaichauffer-kit-completo.zip"
                download
                data-testid="download-kit-zip"
                className="inline-flex items-center gap-3 bg-gold px-8 py-4 text-[11px] uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:bg-bone"
              >
                Scarica tutto — Kit completo (ZIP)
              </a>
            </div>
          </div>
        </FadeUp>

        <div className="mt-20 grid gap-16 lg:grid-cols-2">
          <FadeUp>
            <h2 className="font-serif text-2xl tracking-wide text-bone">COLORI DEL MARCHIO</h2>
            <DrawLine className="mt-6" />
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-5">
              {COLORS.map((c) => (
                <div key={c.hex} data-testid="brand-color-swatch">
                  <div className="h-20 w-full border border-white/10" style={{ backgroundColor: c.hex }} />
                  <p className="mt-3 text-xs text-bone/80">{c.name}</p>
                  <p className="text-[10px] tracking-widest text-mute">{c.hex}</p>
                </div>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <h2 className="font-serif text-2xl tracking-wide text-bone">REGOLE D'USO</h2>
            <DrawLine className="mt-6" />
            <ul className="mt-8 space-y-4 text-sm font-light leading-relaxed text-bone/75">
              {[
                "Rispetta uno spazio vuoto attorno al logo di almeno metà dell'altezza della scritta CHAUFFER.",
                "Non deformare, ruotare o ritoccare il logo; non modificare i colori ufficiali.",
                "Su sfondi scuri usa la versione chiara; su sfondi chiari la versione nera.",
                "Per stampe e insegne preferisci il file SVG (qualità infinita a qualsiasi dimensione).",
              ].map((r) => (
                <li key={r} className="flex gap-3">
                  <span className="mt-2 h-1 w-1 flex-none rotate-45 bg-gold" />
                  {r}
                </li>
              ))}
            </ul>
            <div className="mt-10 border-l-2 border-gold pl-6">
              <p className="font-serif text-lg italic leading-snug text-bone/90">
                Tipografia: Cormorant Garamond (titoli) · Montserrat (testi)
              </p>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
